
import {
  CheckIcon,
  PhoneIncomingIcon,
  ChatAltIcon,
  PlayIcon,
  EyeIcon,
  EyeOffIcon,
  LinkIcon,
  PlusIcon,
} from "@vue-hero-icons/outline";
const CoreDebounceButton = () =>
  import("~~/_core/collections/components/elements/button/DebounceButton.vue");

export default {
  components: {
    CheckIcon,
    PhoneIncomingIcon,
    ChatAltIcon,
    PlayIcon,
    EyeIcon,
    EyeOffIcon,
    LinkIcon,
    PlusIcon,
    CoreDebounceButton,
  },

  layout: "default",

  fetchOnServer: false,

  head() {
    return {
      title: this.metaTitle,
      meta: [
        { hid: "title", name: "title", content: this.metaTitle },
        { hid: "description", name: "description", content: this.metaDesc },
        { hid: "image", name: "image", content: this.metaImage },
        { hid: "og:url", property: "og:url", content: this.routePath },
        { hid: "og:title", property: "og:title", content: this.metaTitle },
        { hid: "og:image", property: "og:image", content: this.metaImage },
        {
          hid: "og:image:secure_url",
          property: "og:image:secure_url",
          content: this.metaImage,
        },
        {
          hid: "og:image:secure",
          property: "og:image:secure",
          content: this.metaImage,
        },
        {
          hid: "og:image:alt",
          property: "og:image:alt",
          content: this.metaTitle,
        },
        {
          hid: "og:description",
          property: "og:description",
          content: this.metaDesc,
        },
        {
          hid: "twitter:title",
          name: "twitter:title",
          content: this.metaTitle,
        },
        {
          hid: "twitter:image:alt",
          name: "twitter:image:alt",
          content: this.metaTitle,
        },
        {
          hid: "twitter:description",
          name: "twitter:description",
          content: this.metaDesc,
        },
        {
          hid: "twitter:image",
          name: "twitter:image",
          content: this.metaImage,
        },
      ],
      link: [
        {
          hid: "canonical",
          rel: "canonical",
          href: `${this.canonical}`,
        },
      ],
      script: [
        {
          innerHTML: `{
            "@context": "http://schema.org",
            "@type": "NewsArticle",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${this.routePath}"
            },
            "headline": "${this.metaTitle}",
            "image": "${this.metaImage}",
            "datePublished": "${this.datePublished}",
            "dateModified": "${this.datePublished}",
            "author": {
              "@type": "Person",
              "name": "${this.$$siteMeta}",
              "url": "${this.routePath}"
            },
            "publisher": {
              "@type": "Organization",
              "name": "${this.$$siteMeta}",
              "logo": {
                "@type": "ImageObject",
                "url": "${this.metaImage}"
              }
            }
          }`,
          type: "application/ld+json",
        },
      ],
      __dangerouslyDisableSanitizers: ["script"],
    };
  },
  data() {
    return {
      // The default value based on timzeone english or vietnamese
      errors: [],
      units: null,
      model: {
        mainProductUnit: "",
        otherProductUnit: "",
        pwdRequired: true,
        orderPending: false,
        password: "",
        seats: "",
        tableNumber: 20,
        tableTakeaway: "",
      },
      showPassword: false,
    };
  },

  async fetch() {
    await this.fetchProductUnits();
    // this.model.pwdRequired = this.$get(this.configs, "[0].pwd_enabled", false);
    this.model.password = this.$get(this.configs, "[0].password", "");
    this.model.tableNumber = this.$get(this.configs, "[0].table_number", 20);
    this.model.seats = this.$get(this.configs, "[0].seats", "");
    this.model.tableTakeaway = this.$get(
      this.configs,
      "[0].table_takeaway",
      ""
    );
    this.model.orderPending = this.$get(
      this.configs,
      "[0].order_status_pending",
      false
    );

    this.model.mainProductUnit = this.$get(this.configs, "[0].unit", "");
    this.model.otherProductUnit = this.$get(this.configs, "[0].other_unit", "");
  },

  activated() {
    this.fetchProductUnits();
  },

  computed: {
    customer() {
      return this.$get(this.$$users, "user", "");
    },

    datePublished() {
      return new Date().toISOString();
    },
    metaTitle() {
      return `${this.$t("meta.invoice.title_config")} | ${this.$$siteMeta}`;
    },

    metaDesc() {
      return `${this.$t("config.sub_title")}| ${this.$$siteShortMeta}`;
    },

    metaImage() {
      return `${this.$config.protocol}://${this.$config.baseHost}/images/metacanhanf.jpeg`;
    },

    routePath() {
      return `http://${this.$config.baseHost}${this.$nuxt.context.route?.fullPath}`;
    },

    canonical() {
      return `https://${this.$config.baseHost}${this.$nuxt.context.route?.fullPath}`;
    },

    configs() {
      return this.$store.getters["config/getConfigs"];
    },

    configSettingsId() {
      return this.$get(this.configs, "[0].id", "");
    },
  },

  methods: {
    goToCategory() {
      this.$router.push("/invoice/category");
    },

    async fetchProductUnits() {
      const { data } = await this.$axios.request({
        url: "/api/menu/unit/",
        method: "get",
        params: {
          page_size: 100,
          page: 1,
        },
      });

      this.units = data?.results;
    },

    validate() {
      const { mainProductUnit, otherProductUnit, tableNumber } = this.model;

      if (mainProductUnit && otherProductUnit && tableNumber) return true;

      this.errors = [];

      if (!mainProductUnit) this.errors.push("mainProductUnit");
      if (!otherProductUnit) this.errors.push("otherProductUnit");
      // if (!password) this.errors.push("password");
      if (!tableNumber) this.errors.push("tableNumber");

      return false;
    },

    async saveConfig() {
      if (!this.validate()) return;

      let apiUrl = "/api/config/";
      let apiMethod = "POST";

      if (this.configSettingsId) {
        apiUrl = `/api/config/${this.configSettingsId}/`;
        apiMethod = "PUT";
      }

      const data = await this.$axios.request({
        url: apiUrl,
        method: apiMethod,
        data: {
          unit: this.model.mainProductUnit,
          other_unit: this.model.otherProductUnit,
          // pwd_enabled: this.model.pwdRequired,
          password: this.model.password,
          table_number: this.model.tableNumber,
          table_takeaway: this.model.tableTakeaway,
          order_status_pending: this.model.orderPending,
          is_active: 1,
        },
      });

      if (data) {
        this.$toast.open({ message: this.$t("common.update_success") });
        this.errors = [];

        // Force refresh configs after save
        await this.$store.dispatch("config/fetchConfigs", true);
        if (this.model.password !== this.$$configSettingPassword) {
          this.$store.dispatch("admin/setAdmin", { signed: 0 });
          this.$store.commit("temp/PUT", { password: "-" });
        }
      }
    },
  },
};
